import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { HelpCircle, ChevronDown, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/faqs`)
      .then(res => {
        setFaqs(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    const category = faq.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(faq);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-cyan"></div>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-electric-cyan/10 border border-electric-cyan/30 rounded-full px-4 py-2 mb-6">
            <HelpCircle className="w-4 h-4 text-electric-cyan" />
            <span className="text-electric-cyan text-sm font-medium">Got Questions?</span>
          </div>
          <h1 className="font-outfit text-4xl md:text-6xl font-bold text-white mb-4" data-testid="faq-title">
            Frequently Asked Questions
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Find answers to common questions about our programs and services
          </p>
        </motion.div>

        {/* FAQ Sections */}
        {Object.entries(groupedFaqs).map(([category, categoryFaqs], categoryIdx) => (
          <motion.div
            key={category}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: categoryIdx * 0.1 }}
            className="mb-12"
          >
            <h2 className="font-outfit text-xl font-semibold text-electric-cyan mb-4">{category}</h2>
            <div className="glass-card overflow-hidden">
              <Accordion type="single" collapsible className="w-full">
                {categoryFaqs.map((faq, idx) => (
                  <AccordionItem 
                    key={faq.id} 
                    value={faq.id}
                    className="border-b border-white/10 last:border-0"
                  >
                    <AccordionTrigger 
                      className="px-6 py-4 text-left text-white hover:text-electric-cyan hover:no-underline"
                      data-testid={`faq-trigger-${idx}`}
                    >
                      <span className="font-medium">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 text-slate-400">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </motion.div>
        ))}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-8 md:p-12 text-center mt-16"
        >
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-slate-400 mb-6">
            We're here to help. Reach out to us for personalized assistance.
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="faq-contact-btn">
            Contact Us <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
